
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import stainlessRailing from "@/assets/stainless-railing.jpg";
import stainlessDoors from "@/assets/stainless-doors.jpg";
import stainlessFacade from "@/assets/stainless-facade.jpg";
import stainlessKitchen from "@/assets/stainless-kitchen.jpg";
import stainlessSink from "@/assets/stainless-sink.jpg";
import stainlessDecor from "@/assets/stainless-decor.jpg";

const StainlessSteel = () => {
  const { language, isRTL } = useLanguage();

  const productSlugs = ["railings", "doors", "facades", "kitchens", "sinks", "decorations"];

  const content = {
    ar: {
      title: "منتجات الاستانلس ستيل",
      subtitle: "نقدم خدمات متكاملة في تصنيع وتركيب منتجات الاستانلس ستيل بأعلى معايير الجودة والأناقة. منتجاتنا تتميز بمقاومة استثنائية للصدأ والتآكل، صلابة عالية، وعمر افتراضي طويل مع سهولة الصيانة والتنظيف.",
      viewDetails: "عرض التفاصيل",
      products: [
        { title: "درابزينات فاخرة", description: "درابزينات استانلس ستيل فاخرة للسلالم والشرفات بتصميمات عصرية وأنيقة. تتميز بالمتانة العالية ومقاومة الصدأ مع الحفاظ على المظهر اللامع لسنوات طويلة." },
        { title: "أبواب وبوابات", description: "أبواب وبوابات أمنية من الاستانلس ستيل بتصاميم عصرية توفر الحماية والأمان مع المظهر الراقي. مناسبة للمباني التجارية والسكنية." },
        { title: "واجهات المباني", description: "واجهات استانلس ستيل للمباني التجارية والفنادق تضفي مظهراً عصرياً وفاخراً. تتميز بمقاومتها للعوامل الجوية وسهولة الصيانة." },
        { title: "تجهيزات المطابخ", description: "تجهيزات متكاملة للمطابخ الصناعية والتجارية من الاستانلس ستيل عالي الجودة. تلبي معايير النظافة والسلامة الغذائية." },
        { title: "أحواض وطاولات", description: "أحواض وطاولات استانلس ستيل خاصة بالمطاعم والمستشفيات والمختبرات. مصممة لتحمل الاستخدام المكثف مع سهولة التنظيف والتعقيم." },
        { title: "ديكورات فخمة", description: "ديكورات داخلية وخارجية من الاستانلس ستيل تضيف لمسة من الفخامة والأناقة. تصاميم مبتكرة تناسب المساحات المختلفة." }
      ]
    },
    en: {
      title: "Stainless Steel Products",
      subtitle: "We offer comprehensive services in manufacturing and installing stainless steel products with the highest quality and elegance standards. Our products feature exceptional rust and corrosion resistance, high hardness, and long lifespan with easy maintenance and cleaning.",
      viewDetails: "View Details",
      products: [
        { title: "Luxury Railings", description: "Luxury stainless steel railings for stairs and balconies with modern and elegant designs. Featuring high durability and rust resistance while maintaining a shiny appearance for years." },
        { title: "Doors & Gates", description: "Security doors and gates made of stainless steel with modern designs providing protection and safety with an upscale appearance. Suitable for commercial and residential buildings." },
        { title: "Building Facades", description: "Stainless steel facades for commercial buildings and hotels adding a modern and luxurious appearance. Featuring weather resistance and easy maintenance." },
        { title: "Kitchen Equipment", description: "Complete equipment for industrial and commercial kitchens made of high-quality stainless steel. Meeting hygiene and food safety standards." },
        { title: "Sinks & Tables", description: "Stainless steel sinks and tables for restaurants, hospitals, and laboratories. Designed to withstand intensive use with easy cleaning and sterilization." },
        { title: "Luxury Decorations", description: "Interior and exterior decorations made of stainless steel adding a touch of luxury and elegance. Innovative designs suitable for different spaces." }
      ]
    }
  };

  const t = content[language];
  const images = [stainlessRailing, stainlessDoors, stainlessFacade, stainlessKitchen, stainlessSink, stainlessDecor];

  return (
    <div className="min-h-screen bg-gray-50 py-12" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-kufi font-bold text-center mb-6 text-primary">{t.title}</h1>
        <p className="text-lg text-gray-700 text-center max-w-3xl mx-auto mb-12">{t.subtitle}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.products.map((product, index) => (
            <motion.div key={product.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }}>
              <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
                <img src={images[index]} alt={product.title} className="w-full h-48 object-cover" />
                <CardContent className="p-6 flex-grow flex flex-col">
                  <h3 className="text-xl font-kufi font-bold mb-2">{product.title}</h3>
                  <p className="text-gray-600 mb-4 flex-grow">{product.description}</p>
                  <Link to={`/stainless-steel/${productSlugs[index]}`}>
                    <Button className="bg-primary hover:bg-primary/90 w-full">{t.viewDetails}</Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default StainlessSteel;
